import { useCallback, useEffect, useState } from 'react';

import { END_OF_DAY_TIME, START_OF_DAY_TIME } from '../../constants';
import { DEFAULT_AMOUNT_OF_LOGS_TO_SHOW, INITIAL_DATE_RANGE, INITIAL_PAGINATION_OPTIONS } from '../constants';
import type { FilterResult, UseDateRangeFilterProps } from './types';
import { convertLocalDateToUTC } from './utils';

export const useDateRangeFilter = ({
  setQueryVariables,
  id,
  setLoadingFilter,
  toggleAll,
  setDesiredItemsToShow,
}: UseDateRangeFilterProps): FilterResult => {
  const [dateRange, setDateRange] = useState(INITIAL_DATE_RANGE);

  useEffect(() => {
    setDateRange(INITIAL_DATE_RANGE);
  }, [id]);

  const onDateRangeChange = useCallback(
    (field: 'start' | 'end', value: string) => {
      setDateRange((prev) => ({
        ...prev,
        [`${field}Date`]: value ? value : '',
        [`${field}DateTime`]: value ? value : '',
      }));
    },
    [setDateRange]
  );

  const onFilterSubmit = useCallback((): void => {
    setLoadingFilter(true);
    toggleAll(false);

    setQueryVariables((prevVariables) => ({
      ...prevVariables,
      paginationOptions: INITIAL_PAGINATION_OPTIONS,
      filterOptions: {
        dateRange: {
          startDate: convertLocalDateToUTC(`${dateRange.startDate}${START_OF_DAY_TIME}`),
          endDate: dateRange.endDate ? convertLocalDateToUTC(`${dateRange.endDate}${END_OF_DAY_TIME}`) : null,
        },
      },
    }));
    setDesiredItemsToShow(DEFAULT_AMOUNT_OF_LOGS_TO_SHOW);
  }, [setQueryVariables, dateRange.endDate, dateRange.startDate, setDesiredItemsToShow, setLoadingFilter, toggleAll]);

  const onClearFilter = useCallback((): void => {
    setLoadingFilter(true);
    toggleAll(false);
    setDateRange(INITIAL_DATE_RANGE);
    setQueryVariables((prevVariables) => ({
      ...prevVariables,
      filterOptions: {},
      paginationOptions: INITIAL_PAGINATION_OPTIONS,
    }));
    setDesiredItemsToShow(DEFAULT_AMOUNT_OF_LOGS_TO_SHOW);
  }, [setLoadingFilter, toggleAll, setQueryVariables, setDesiredItemsToShow]);

  return { dateRange, onDateRangeChange, onFilterSubmit, onClearFilter };
};
